import React, { useEffect, useRef, useState } from 'react';
import { Alert, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, TextField } from '@mui/material';
import { ScribeButton as Button } from './ScribeUI';
import { supabase } from '../../supabaseClient';

function hasContent(html) {
  const document = new DOMParser().parseFromString(html || '', 'text/html');
  document.querySelectorAll('script,style').forEach(node => node.remove());
  return Boolean(document.body.textContent.replace(/[\s\u200b-\u200d\ufeff]/g, '') || document.body.querySelector('img[src]'));
}

// `hideTrigger` + `openSignal`: the report panel opens this from its ⋯ menu instead of a button.
export default function SaveAsTemplateButton({ getContent, userDetails, onTemplateSaved, disabled = false, hideTrigger = false, openSignal = 0 }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const busy = useRef(false);
  const owner = useRef(null);
  const currentUser = useRef(userDetails?.id);
  currentUser.current = userDetails?.id;
  const hasVisibleContent = hasContent(content);
  const openDialog = () => { owner.current = userDetails.id; setContent(getContent?.() || ''); setTitle(''); setError(''); setOpen(true); };
  useEffect(() => {
    if (openSignal && !disabled && userDetails?.id) openDialog();
  }, [openSignal]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async event => {
    event.preventDefault();
    if (busy.current || !title.trim() || !hasVisibleContent || disabled || !userDetails?.id || owner.current !== userDetails.id) return;
    busy.current = true; setSaving(true); setError('');
    const template = { title: title.trim(), content, userid: userDetails.id, created_by: userDetails.name || '', is_base_template: false };
    try {
      const { data, error: insertError } = await supabase.from('templates').insert([template]).select('id,created_at,updated_at').single();
      if (insertError || !data?.id) throw insertError || new Error('No template was returned');
      // Encryption triggers may transform returned text; keep the user's plaintext.
      if (currentUser.current !== template.userid) { setOpen(false); return; }
      onTemplateSaved?.({ ...template, id: data.id, created_at: data.created_at, updated_at: data.updated_at });
      setOpen(false); setSaved(true);
    } catch {
      setError('Could not save template. Please try again.');
    } finally { busy.current = false; setSaving(false); }
  };
  return <>
    {!hideTrigger && <Button variant="outlined" size="small" disabled={disabled || !userDetails?.id} onClick={openDialog}>Save as a template</Button>}
    <Dialog open={open} onClose={() => { if (!busy.current) setOpen(false); }} fullWidth maxWidth="sm">
      <form aria-label="Save template" onSubmit={save}>
        <DialogTitle>Save as a template</DialogTitle>
        <DialogContent>
          <TextField autoFocus required fullWidth margin="dense" label="Template title" value={title} disabled={saving} onChange={event => setTitle(event.target.value)} />
          {!hasVisibleContent && <Alert severity="info" sx={{ mt: 2 }}>Add content before saving a template.</Alert>}
          {owner.current !== userDetails?.id && <Alert severity="warning">Your account changed. Close this dialog and try again.</Alert>}
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions><Button disabled={saving} onClick={() => setOpen(false)}>Cancel</Button><Button type="submit" variant="contained" disabled={saving || disabled || !title.trim() || !hasVisibleContent || !userDetails?.id || owner.current !== userDetails.id}>{saving ? 'Saving…' : 'Save template'}</Button></DialogActions>
      </form>
    </Dialog>
    <Snackbar open={saved} autoHideDuration={4000} onClose={() => setSaved(false)}><Alert severity="success" onClose={() => setSaved(false)}>Template saved</Alert></Snackbar>
  </>;
}
